import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../Spinner';
import { PortalReportsApi } from '../../../api/portalReports';
import { loadFieldsCatalog } from '../../../utils/fieldsCatalog';
import { isNetworkError } from '../../../utils/apiResponse';
import { DataExplorerChartsSection } from '../../data_explorer/DataExplorerChartsSection';
import { DataExplorerLineChart } from '../../data_explorer/DataExplorerLineChart';
import { DataExplorerTable } from '../../data_explorer/DataExplorerTable';

/**
 * Renderiza un reporte del portal del cliente reusando los MISMOS sub-componentes
 * del Data Explorer (KPIs + bar/pie + line chart + tabla). Cualquier mejora visual
 * que se haga al Explorer aparece automáticamente acá.
 *
 * Cambios respecto del Explorer:
 *  - No mostramos los `ChartControls` (dropdowns que disparan re-query). El cliente
 *    final solo puede modificar el rango de fechas; el resto del config viene del
 *    snapshot persistido en `view.config`.
 *  - El `queryFn` se inyecta como prop para soportar tanto `queryMyReport` (cliente)
 *    como `queryPreviewReport` (agency en preview-as-client).
 */
/**
 * Qué lee el CLIENTE cuando su reporte no se pudo calcular.
 *
 * ⚠️ Nunca el `error_message` crudo, que es lo que se hacía hasta la card `4By6KR9g`. Para una
 * cuenta desconectada, el texto del nodo enumera las cuentas publicitarias del cliente **con canal
 * y Account ID** («… - Ad Account (FACEBOOK, 2283312421992046) - SIN ACCESO desde el 03/05/2026»),
 * y ése es el mismo criterio por el que Informes reemplaza el texto del backend antes de mandarlo
 * al portal. Acá se resuelve por código tipado contra i18n, y lo desconocido cae al genérico.
 */
function portalErrorText(response, t) {
    const code = response && (response.error_code || response.status);
    if (typeof code === 'string' && code !== 'ERROR') {
        return t(`portal_informes.warnings.${code}.body`, t('portal_editorial.fetch_error'));
    }
    return t('portal_editorial.fetch_error');
}

export function PortalReportRenderer({ report, dateRangeOverride, queryFn, headerRight }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [fieldsData, setFieldsData] = useState(null);

    useEffect(() => {
        loadFieldsCatalog().then(setFieldsData);
    }, []);

    const reportId = report?.id;
    const overrideKey = JSON.stringify(dateRangeOverride || null);

    useEffect(() => {
        if (!reportId) return;
        setLoading(true);
        setError(null);
        const overridePayload = buildDateRangeOverridePayload(JSON.parse(overrideKey));
        const fetcher = queryFn || PortalReportsApi.queryMyReport;
        fetcher(reportId, overridePayload)
            .then((response) => {
                // ⚠️ La guarda NO puede ser `status === 'ERROR'` a secas: el `NETWORK_ERROR`
                // sintético del `APIClient` trae `status: 'NETWORK_ERROR'`, no matchea, y el objeto
                // de error entraba al render como si fuera un resultado — `DataExplorerTable` se
                // quedaba sin `tableData` y el CLIENTE leía «No hay datos para mostrar con los
                // filtros seleccionados» sobre una conexión caída. Es el defecto de la card
                // `4By6KR9g` otra vez, y la 6ª pantalla de la clase de FRONTEND-47.
                //
                // Tampoco sirve `status !== 'OK'`: este endpoint devuelve el payload PELADO, sin
                // `status`, en el camino de éxito. La condición va por la FORMA —`tableData`, que
                // el view escribe siempre y que con cero filas igual llega como `{headers, rows}`—
                // así que una consulta legítimamente vacía sigue diciendo «No hay datos».
                if (!response || response.status === 'ERROR' || isNetworkError(response)
                    || !response.tableData) {
                    setError(portalErrorText(response, t));
                    setLoading(false);
                    return;
                }
                setResult(response);
                setLoading(false);
            })
            .catch(() => {
                setError(t('portal_editorial.fetch_error'));
                setLoading(false);
            });
    }, [reportId, overrideKey, t, queryFn]);

    // Preferencias de visualización (qué métricas van en los KPIs, qué dimensión/métrica
    // en barras y torta) tal como las dejó la agencia al guardar la vista. En el Explorer
    // los widgets las leen de LocalStorage, pero el cliente final nunca pasó por ahí: sin
    // pasárselas explícitamente veía el default posicional (primeras 4 métricas, última
    // dimensión) en vez de lo que la agencia configuró.
    const chartPrefs = useMemo(
        () => (report?.config ? (report.config.chart_preferences || {}) : null),
        [report]
    );

    // Reconstruye el shape que esperan los componentes del Explorer (`lastFilters`)
    // a partir del snapshot persistido en `view.config`. Sin esto, KPIs/bar/pie no
    // saben qué métricas y dimensiones priorizar para sus dropdowns iniciales.
    const lastFilters = useMemo(() => {
        if (!report?.config) return null;
        const config = report.config;
        return {
            agencyClientId: report.agency_client_id,
            sourceType: config.source_type || config.sourceType,
            selectedItems: config.selected_items || config.selectedItems || [],
            compare: !!config.compare,
            dimensions: config.dimensions || [],
            metrics: (config.metrics || []).map((m) => (typeof m === 'string' ? m : m.name)),
            dimensionsFilters: config.dimensions_filters || config.dimensionsFilters || [],
        };
    }, [report]);

    // Métricas por default del line chart: las que el backend marcó `is_default`.
    // Si ninguna está marcada, agarramos las primeras 2.
    const selectedChartMetrics = useMemo(() => {
        const metrics = result?.chartData?.metrics;
        if (!Array.isArray(metrics) || metrics.length === 0) return [];
        const defaults = metrics.filter((m) => m.is_default).map((m) => m.name);
        if (defaults.length > 0) return defaults;
        return metrics.slice(0, 2).map((m) => m.name);
    }, [result]);

    if (!report) return null;

    return (
        <article className="portal-report-article">
            <header className="portal-report-article-header">
                <div className="portal-report-article-header-text">
                    <h2 className="portal-report-article-title">{report.name}</h2>
                    {report.description && (
                        <p className="portal-report-article-desc">{report.description}</p>
                    )}
                </div>
                {headerRight && (
                    <div className="portal-report-article-header-right">
                        {headerRight}
                    </div>
                )}
            </header>

            {loading && (
                <div className="portal-report-loading"><Spinner /></div>
            )}

            {error && !loading && (
                <div className="portal-report-error">{error}</div>
            )}

            {!loading && !error && result && lastFilters && fieldsData && (
                <div className="portal-report-explorer-mirror">
                    <DataExplorerChartsSection
                        results={result}
                        lastFilters={lastFilters}
                        fieldsData={fieldsData}
                        chartPrefs={chartPrefs}
                        onChartPrefsChange={NOOP}
                        viewMode="client"
                    />

                    {result.chartData && result.chartData.series && result.chartData.series.length > 0 && (
                        <DataExplorerLineChart
                            chartData={result.chartData}
                            selectedMetrics={selectedChartMetrics}
                            showTooltip={false}
                        />
                    )}

                    <DataExplorerTable
                        loading={false}
                        results={result}
                        fieldsData={fieldsData}
                    />
                </div>
            )}
        </article>
    );
}

function buildDateRangeOverridePayload(override) {
    if (!override) return null;
    if (override.predefinedPeriod) return { predefinedPeriod: override.predefinedPeriod };
    if (override.startDate && override.endDate) {
        return { startDate: override.startDate, endDate: override.endDate };
    }
    return null;
}

const NOOP = () => {};
